import { formatNumber } from '@/common/helpers/formatUtilities'

import { PRODUCT_DETAILS, RESELLERS } from '../Constants/Constants'

// Constants
const MM_TO_INCHES_RATIO = 25.4
const ISSUE_SEVERITY = {
    issue: 0,
    warning: 1,
}
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const CAPITAL_LETTERS_REGEX = /([A-Z])/g
const LEADING_KEBAB_SEPARATOR_REGEX = /^-/
const LEADING_SPACE_AND_LOWERCASE_REGEX = /^ [a-z]/
/**
 * Maximum allowed file size for drawing uploads (4MB in bytes).
 * @constant {number}
 */
export const MAX_DRAWING_UPLOAD_SIZE_BYTES = 4194304

/**
 * Generates a timestamp string in YYYYMMDDHHMI format for the current date and time.
 * @returns {string} Current timestamp formatted as YYYYMMDDHHMI
 * @example
 * // Returns something like "202403151430" for March 15, 2024 at 2:30 PM
 * const timestamp = currentDateTimeString();
 */
export function currentDateTimeString() {
    const now = new Date()

    return (
        now.getFullYear() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0')
    )
}

/**
 * Checks if a string is empty, null, undefined, or contains only whitespace.
 * @param {string|null|undefined} str - The string to check
 * @returns {boolean} True if the string is empty or whitespace-only, false otherwise
 * @example
 * stringIsEmptyOrWhitespace('') // returns true
 * stringIsEmptyOrWhitespace('   ') // returns true
 * stringIsEmptyOrWhitespace('hello') // returns false
 * stringIsEmptyOrWhitespace(null) // returns true
 */
export function stringIsEmptyOrWhitespace(str) {
    return typeof str === 'undefined' || str === null || str.trim() === ''
}

/**
 * Retrieves the value of a cookie by its name.
 * @param {string} cookieName - The name of the cookie to retrieve
 * @returns {string|null} The cookie value, or null if not found
 * @example
 * getCookieByName('sessionId') // returns 'abc123' or null
 */
export function getCookieByName(cookieName) {
    if (!cookieName || typeof document === 'undefined') {
        return null
    }

    const name = cookieName + '='
    const decodedCookie = decodeURIComponent(document.cookie)
    const cookies = decodedCookie.split(';')

    for (const cookie of cookies) {
        const trimmedCookie = cookie.trim()
        if (trimmedCookie.indexOf(name) === 0) {
            return trimmedCookie.substring(name.length)
        }
    }

    return null
}

/**
 * Converts inches to millimeters.
 * @param {number} inches - The measurement in inches
 * @returns {number} The measurement in millimeters
 * @example
 * inchesToMm(1) // returns 25.4
 */
export function inchesToMm(inches) {
    return inches * MM_TO_INCHES_RATIO
}

/**
 * Converts millimeters to inches.
 * @param {number} mm - The measurement in millimeters
 * @returns {number} The measurement in inches
 * @example
 * mmToInches(25.4) // returns 1
 */
export function mmToInches(mm) {
    return mm / MM_TO_INCHES_RATIO
}

/**
 * Checks if a quote item can fit on its associated sheet.
 * @param {Object} quoteItem - The quote item to check
 * @param {string} organisationDrawingUnits - The organisation's drawing units ('imperial' or 'metric')
 * @returns {boolean} True if the quote item can fit on the sheet
 * @example
 * canQuoteItemFitOnSheet(quoteItem, 'metric') // returns true/false
 */
export const canQuoteItemFitOnSheet = (quoteItem, organisationDrawingUnits) => {
    if (!quoteItem || (!quoteItem.minimumBoundBoxWidth && !quoteItem.profile)) {
        return false
    }

    return canPartFitOnSheet(
        {
            drawing: {
                width: quoteItem.minimumBoundBoxWidth,
                height: quoteItem.minimumBoundBoxHeight,
            },
            isImperial: quoteItem.isImperial,
            material: {
                sheetSize: {
                    width: quoteItem.sheet?.sheetWidth,
                    height: quoteItem.sheet?.sheetHeight,
                },
            },
        },
        organisationDrawingUnits,
        quoteItem.profile
    )
}

/**
 * Determines if a part can fit on a sheet, considering rotation if necessary.
 * @param {Object} partData - The part data object
 * @param {Object} partData.drawing - Drawing dimensions
 * @param {number} [partData.drawing.height=0] - Part height
 * @param {number} [partData.drawing.width=0] - Part width
 * @param {boolean} partData.isImperial - Whether part uses imperial units
 * @param {Object} partData.material - Material information
 * @param {Object} [partData.material.sheetSize] - Sheet dimensions
 * @param {string} organisationDrawingUnits - Organisation's drawing units
 * @param {boolean} isRotary - Whether this is a rotary part
 * @returns {boolean} True if the part can fit on the sheet
 * @example
 * canPartFitOnSheet(partData, 'metric', false) // returns true/false
 */
export function canPartFitOnSheet(
    {
        drawing: { height: partHeight = 0, width: partWidth = 0 },
        isImperial: partIsImperial,
        material: {
            sheetSize = {
                width: 0,
                height: 0,
            },
        },
    },
    organisationDrawingUnits,
    isRotary
) {
    // Rotary parts always fit
    if (isRotary) {
        return true
    }

    const convertedSheetSize = convertSheetSize(
        sheetSize,
        partIsImperial,
        organisationDrawingUnits?.toLowerCase() === 'imperial'
    )

    const largestPartSide = Math.max(partWidth, partHeight)
    const smallestPartSide = Math.min(partWidth, partHeight)
    const largestSheetSide = Math.max(convertedSheetSize?.width, convertedSheetSize?.height)
    const smallestSheetSide = Math.min(convertedSheetSize?.width, convertedSheetSize?.height)

    // Check if part fits without rotation
    const canFitWithoutRotation = largestPartSide <= largestSheetSide && smallestPartSide <= smallestSheetSide

    // If it doesn't fit without rotation, check with rotation
    return (
        canFitWithoutRotation ||
        partCanFitWithRotation(largestSheetSide, smallestSheetSide, largestPartSide, smallestPartSide)
    )
}

/**
 * Determines if a rectangle can fit inside another rectangle with rotation allowed.
 * Based on the mathematical formula from https://www.jstor.org/stable/2691523
 * @param {number} containerLargerSide - Larger side of the container rectangle
 * @param {number} containerSmallerSide - Smaller side of the container rectangle
 * @param {number} partLargerSide - Larger side of the part rectangle
 * @param {number} partSmallerSide - Smaller side of the part rectangle
 * @returns {boolean} True if the part can fit in the container with rotation
 * @example
 * partCanFitWithRotation(100, 50, 80, 60) // returns true/false
 */
export function partCanFitWithRotation(containerLargerSide, containerSmallerSide, partLargerSide, partSmallerSide) {
    // Simplified variable names for the mathematical formula
    const a = containerLargerSide
    const b = containerSmallerSide
    const p = partLargerSide
    const q = partSmallerSide

    // Check if part fits with rotation using the mathematical formula
    return (
        q <= b && (p <= a || b * (p * p + q * q) >= 2 * p * q * a + (p * p - q * q) * Math.sqrt(p * p + q * q - a * a))
    )
}

/**
 * Converts sheet dimensions between imperial and metric units based on part and sheet unit systems.
 * @param {Object} sheetSize - The sheet dimensions
 * @param {number} sheetSize.height - Sheet height
 * @param {number} sheetSize.width - Sheet width
 * @param {boolean} partIsImperial - Whether the part uses imperial units
 * @param {boolean} sheetIsImperial - Whether the sheet uses imperial units
 * @returns {Object} Converted sheet size with width and height properties
 * @example
 * convertSheetSize({width: 100, height: 200}, true, false) // converts mm to inches
 */
export function convertSheetSize({ height, width }, partIsImperial, sheetIsImperial) {
    // If both use the same unit system, no conversion needed
    if (partIsImperial === sheetIsImperial) {
        return { width, height }
    }

    const conversionFn = partIsImperial ? mmToInches : inchesToMm

    return {
        width: conversionFn(width),
        height: conversionFn(height),
    }
}

/**
 * Checks if a quote item has any validation errors.
 * @param {Object} quoteItem - The quote item to validate
 * @returns {boolean} True if the quote item has any errors
 * @example
 * quoteItemHasErrors(quoteItem, 'metric', severityDict) // returns true/false
 */
export function quoteItemHasErrors(quoteItem) {
    const materialError =
        !quoteItem.cuttingTechnologyId || !quoteItem.materialId || !quoteItem.sheetId || !quoteItem.thickness
    const quantityError = quoteItem.quantity <= 0

    return materialError || quantityError
}

/**
 * Checks if a quote item has any severe validation issues.
 * @param {Object} quoteItem - The quote item to check
 * @returns {boolean} True if there are severe validation issues
 * @example
 * hasSevereValidationIssues(quoteItem) // returns true/false
 */
export function hasSevereValidationIssues(quoteItem) {
    const validationIssues = quoteItem.quoteItemMetadata?.quoteItemValidationIssues
    return (
        Array.isArray(validationIssues) &&
        validationIssues.length > 0 &&
        validationIssues.some((issue) => issue.severity === 'Error')
    )
}

// Validation issue type to message mapping
const VALIDATION_ISSUE_MESSAGES = {
    ParametersInvalid: 'Cutting time cannot be calculated',
    CuttingTechnologyProfileConstraints: 'Material length exceeds cutting technology size constraints',
    CuttingTechnologyConstraints: 'Material size exceeds cutting technology size constraints',
    SheetConstraints: 'Part cannot fit on material',
    NumberOfPartsOrRuntimeInvalid: 'Part nesting or cutting time cannot be calculated',
    SheetWithWebConstraints: 'Part with web cannot fit on material',
    SheetWithWebAndChuckAllowanceConstraints: 'Part with chuck allowance cannot fit on material',
    NoCuttingTechnologySelected: 'Cutting Technology is required',
    NoSheetSelected: 'Sheet is required',
    SheetExpired: 'Selected sheet has expired',
    NoMatchingRate: 'No rate entry found for sheet selection. Check rate table.',
    HasSmallHoles: 'Part contains profiles too small to process',
    NoMaterialSelected: 'Material is required',
    NoThicknessSelected: 'Thickness is required',
}

/**
 * Gets a human-readable message for a validation issue.
 * @param {Object} issue - The validation issue object
 * @param {string} issue.type - The issue type
 * @param {string} [issue.message] - Fallback message if type not recognized
 * @returns {string} Human-readable error message
 * @example
 * getMessageForValidationIssue({type: 'NoMaterialSelected'}) // returns 'Material is required'
 */
export const getMessageForValidationIssue = (issue) => {
    return VALIDATION_ISSUE_MESSAGES[issue.type] || issue.message || 'Unknown validation issue'
}

export function sheetCanBeCut(sheet, cuttingTech, rateTable, rates) {
    const errorOutput = { errorMessage: '' }
    const canBeCut =
        sheetHasRateTable(rateTable, errorOutput) &&
        sheetCanBeUsedWithCuttingTech(sheet, cuttingTech, errorOutput) &&
        sheetHasRateTableThickness(sheet, rateTable, rates, errorOutput)
    return { canBeCut: canBeCut, errorMessage: errorOutput.errorMessage }
}

const sheetCanBeUsedWithCuttingTech = (sheet, cuttingTech, errorOutput) => {
    let length = sheet.sheetWidth
    let width = sheet.sheetHeight
    if (width > length) {
        const tmp = length
        length = width
        width = tmp
    }

    const sheetNotLargerThanMax =
        length <= (cuttingTech?.maximumSheetLength ? cuttingTech?.maximumSheetLength : length) &&
        width <= (cuttingTech?.maximumSheetWidth ? cuttingTech?.maximumSheetWidth : width)
    if (!sheetNotLargerThanMax) {
        errorOutput.errorMessage = 'Sheet size is larger than the cutting technology’s maximum settings'
    }
    return sheetNotLargerThanMax
}

const sheetHasRateTable = (rateTable, errorOutput) => {
    if (!rateTable) {
        errorOutput.errorMessage = 'No rate table assigned'
        return false
    } else if (rateTable.isDeleted) {
        errorOutput.errorMessage =
            'The assigned rate table has been archived. Unarchive the rate table or assign a new one.'
        return false
    }
    return true
}

const sheetHasRateTableThickness = (sheet, rateTable, rates, errorOutput) => {
    if (rateTable) {
        const sortedRates = Object.values(rates).filter(
            (rate) => rate.rateTableId === rateTable.rateTableId && !rate.isDeleted
        )
        sortedRates.sort((a, b) => a.thickness - b.thickness)
        const rateTableHasSheetThickness =
            sheet.thickness >= sortedRates[0]?.thickness &&
            sheet.thickness <= sortedRates[sortedRates.length - 1]?.thickness
        if (!rateTableHasSheetThickness) {
            errorOutput.errorMessage = 'Sheet thickness not present in rate table'
        }
        return rateTableHasSheetThickness
    }
    return false
}

/**
 * Converts PascalCase text to kebab-case.
 * @param {string} text - The PascalCase text to convert
 * @returns {string} The kebab-case version of the text
 * @example
 * pascalToKebabCase('MyComponentName') // returns 'my-component-name'
 */
export function pascalToKebabCase(text) {
    return text
        .replace(CAPITAL_LETTERS_REGEX, (match) => '-' + match.toLowerCase())
        .replace(LEADING_KEBAB_SEPARATOR_REGEX, '')
}

/**
 * Converts PascalCase text to sentence case.
 * @param {string} text - The PascalCase text to convert
 * @returns {string} The sentence case version of the text
 * @example
 * pascalToSentenceCase('MyComponentName') // returns 'My component name'
 */
export function pascalToSentenceCase(text) {
    return text
        .replace(CAPITAL_LETTERS_REGEX, (match) => ' ' + match.toLowerCase())
        .replace(LEADING_SPACE_AND_LOWERCASE_REGEX, (substring) => substring.substring(1).toUpperCase())
}

/**
 * Validates if an email address has the correct format.
 * @param {string} email - The email address to validate
 * @returns {boolean} True if the email format is valid
 * @example
 * validateEmail('user@example.com') // returns true
 * validateEmail('invalid-email') // returns false
 */
export function validateEmail(email) {
    return EMAIL_REGEX.test(email)
}

/**
 * Splits comma-separated email input into trimmed, non-empty addresses.
 * @param {string} [value=''] - Raw text from an email field
 * @returns {string[]} The individual addresses
 */
export function splitEmailAddresses(value = '') {
    return value
        .split(',')
        .map((address) => address.trim())
        .filter(Boolean)
}

/**
 * Converts a percentage to a decimal fraction.
 * @param {number|null|undefined} percentage - The percentage value to convert
 * @param {number} [numberOfDecimalPlaces=4] - Number of decimal places to round to
 * @returns {number|undefined} The decimal fraction, or undefined if input is null/undefined
 * @example
 * percentageToFraction(50) // returns 0.5
 * percentageToFraction(33.33, 2) // returns 0.33
 */
export function percentageToFraction(percentage, numberOfDecimalPlaces = 4) {
    return percentage != null ? parseFloat((percentage / 100).toFixed(numberOfDecimalPlaces)) : undefined
}

/**
 * Converts a decimal fraction to a percentage.
 * @param {number|null|undefined} fraction - The fraction value to convert
 * @param {number} [numberOfDecimalPlaces=4] - Number of decimal places to round to
 * @returns {number|undefined} The percentage value, or undefined if input is invalid
 * @example
 * fractionToPercentage(0.5) // returns 50
 * fractionToPercentage(0.3333, 2) // returns 33.33
 */
export function fractionToPercentage(fraction, numberOfDecimalPlaces = 4) {
    return fraction != null && !isNaN(fraction) && isFinite(fraction)
        ? parseFloat((fraction * 100).toFixed(numberOfDecimalPlaces))
        : undefined
}

/**
 * Determines the appropriate number of decimal places to display based on the precision of a decimal number.
 * @param {number|null|undefined} decimalNumber - The decimal number to analyze
 * @returns {number} 4 if the number has 3+ decimal places, 2 if it has 1-2 decimal places, 0 otherwise
 * @example
 * getDecimalPlacesForDisplay(0.12345) // returns 4
 * getDecimalPlacesForDisplay(0.12) // returns 2
 * getDecimalPlacesForDisplay(0.1) // returns 2
 * getDecimalPlacesForDisplay(1) // returns 0
 * getDecimalPlacesForDisplay(null) // returns 0
 */
export function getDecimalPlacesForDisplay(decimalNumber) {
    if (decimalNumber == null || isNaN(decimalNumber)) {
        return 0
    }

    const decimalStr = decimalNumber.toString()
    const decimalIndex = decimalStr.indexOf('.')

    if (decimalIndex === -1) {
        return 0
    }

    const decimalPlaces = decimalStr.length - decimalIndex - 1

    if (decimalPlaces >= 3) {
        return 4
    } else if (decimalPlaces >= 1) {
        return 2
    }

    return 0
}

/**
 * Extracts the locale from an organisation object.
 * @param {Object} organisation - The organisation object
 * @returns {string|undefined} The organisation's locale
 * @example
 * getLocaleToUse({locale: 'en-US'}) // returns 'en-US'
 */
export function getLocaleToUse(organisation) {
    return organisation?.locale
}

/**
 * Determines the language to use, preferring user language over organisation language.
 * @param {Object} user - The user object
 * @param {Object} organisation - The organisation object
 * @returns {string|undefined} The language to use
 * @example
 * getLanguageToUse({language: 'en'}, {language: 'fr'}) // returns 'en'
 */
export function getLanguageToUse(user, organisation) {
    return user?.language || organisation?.language
}

/**
 * Triggers a file download in the browser using a blob.
 * @param {*} payload - The file content to download
 * @param {string} filename - The name for the downloaded file
 * @param {string} [filetype=''] - The MIME type of the file
 * @example
 * browserFileDownload('Hello World', 'greeting.txt', 'text/plain')
 */
export function browserFileDownload(payload, filename, filetype = '') {
    const url = window.URL.createObjectURL(new Blob([payload], { type: filetype }))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}

/**
 * Maps locale IDs to supported locales, defaulting to en-US for unsupported locales.
 * @param {string} localeId - The locale ID to map
 * @returns {string} The supported locale identifier
 * @example
 * getSupportedLocale('fr-FR') // returns 'fr'
 * getSupportedLocale('unsupported') // returns 'en-US'
 */
export const getSupportedLocale = (localeId) => {
    const localeMap = {
        'en-AU': 'en-AU',
        'gsw-FR': 'fr',
        'br-FR': 'fr',
        'ca-FR': 'fr',
        'co-FR': 'fr',
        'fr-FR': 'fr',
        'ia-FR': 'fr',
        'oc-FR': 'fr',
        'en-DE': 'de',
        'de-DE': 'de',
        'nds-DE': 'de',
        'dsb-DE': 'de',
        'ksh-DE': 'de',
        'hsb-DE': 'de',
        'ca-IT': 'it',
        'fur-IT': 'it',
        'de-IT': 'it',
        'it-IT': 'it',
        'en-NZ': 'en-AU',
        'mi-NZ': 'en-AU',
        'kw-GB': 'en-GB',
        'en-GB': 'en-GB',
        'gd-GB': 'en-GB',
        'cy-GB': 'en-GB',
        'es-ES': 'es',
        'pl-PL': 'pl',
        'pt-PT': 'pt',
        'pt-BR': 'pt',
        es: 'es',
        fr: 'fr',
        de: 'de',
        it: 'it',
        pt: 'pt',
        pl: 'pl',
    }

    const mappedLocale = localeMap[localeId]

    if (!mappedLocale) {
        return 'en-US'
    } else {
        return mappedLocale
    }
}

/**
 * Retrieves product details based on the reseller name.
 * @param {string} [resellerName] - The reseller name, defaults to VITE_RESELLER env var
 * @returns {Object} Product details object
 * @example
 * getProductDetails('acme') // returns RESELLERS['acme'] or PRODUCT_DETAILS
 */
export function getProductDetails(resellerName = import.meta.env?.VITE_RESELLER) {
    return resellerName ? RESELLERS[resellerName] : PRODUCT_DETAILS
}

/**
 * Determines if an organisation uses imperial units.
 * @param {Object} organisation - The organisation object
 * @returns {boolean} True if the organisation uses imperial units
 * @example
 * organisationIsImperial({defaultDrawingUnits: 'imperial'}) // returns true
 */
export function organisationIsImperial(organisation) {
    return (
        organisation?.defaultDrawingUnits?.localeCompare('imperial', undefined, {
            sensitivity: 'accent',
        }) === 0
    )
}

/**
 * Gets the appropriate length precision based on organisation's unit system.
 * @param {Object} organisation - The organisation object
 * @returns {number} 4 for imperial units, 2 for metric units
 * @example
 * organisationLengthPrecision({defaultDrawingUnits: 'imperial'}) // returns 4
 */
export function organisationLengthPrecision(organisation) {
    return organisationIsImperial(organisation) ? 4 : 2
}

/**
 * Gets the appropriate dimension tolerance based on organisation's unit system.
 * @param {Object} organisation - The organisation object
 * @returns {number} 0.0001 for imperial units, 0.01 for metric units
 * @example
 * organisationDimensionTolerance({defaultDrawingUnits: 'metric'}) // returns 0.01
 */
export function organisationDimensionTolerance(organisation) {
    return organisationIsImperial(organisation) ? 0.0001 : 0.01
}

/**
 * Checks if two numbers are within a specified tolerance percentage.
 * @param {number} number1 - First number to compare
 * @param {number} number2 - Second number to compare
 * @param {number} tolerance - Tolerance percentage (0-100)
 * @returns {boolean} True if numbers are within tolerance
 * @example
 * areNumbersWithinTolerance(100, 105, 10) // returns true (5% difference < 10%)
 */
export function areNumbersWithinTolerance(number1, number2, tolerance) {
    const percentageDifference = 100 * (1 - Math.min(number1, number2) / Math.max(number1, number2))
    return percentageDifference <= tolerance
}

/**
 * Gets the Nth shortest dimension for rotary parts (1-indexed).
 * For rotary parts, dimensions are sorted and the Nth smallest is returned.
 * @param {Object} quoteItem - The quote item to analyze
 * @param {number} [nthPosition=2] - Which position to return (1=shortest, 2=second shortest, etc.)
 * @returns {number|null} The Nth shortest dimension, or null if not enough dimensions
 * @example
 * getRotaryNthShortestDimension(rotaryQuoteItem, 1) // returns shortest dimension
 * getRotaryNthShortestDimension(rotaryQuoteItem, 2) // returns second shortest dimension
 */
export const getRotaryNthShortestDimension = (quoteItem, nthPosition = 2) => {
    if (!quoteItem || !('profile' in quoteItem) || !quoteItem.profile) {
        return null
    }

    if (nthPosition < 1) {
        return null
    }

    const { rotaryPartLength, sheet } = quoteItem

    // Collect all valid rotary dimensions
    const rotaryValues = [sheet?.sheetWidth, sheet?.sheetHeight, sheet?.diameter, rotaryPartLength].filter(
        (val) => val != null && val > 0
    )

    // Need at least nthPosition dimensions to get the requested position
    if (rotaryValues.length < nthPosition) {
        return null
    }

    // Sort dimensions in ascending order
    const sortedValues = rotaryValues.sort((a, b) => a - b)

    // Return the Nth smallest (index -nthPosition)
    return sortedValues.at(-nthPosition) || null
}

/**
 * Calculates dimensions for quote items, converting units as needed.
 * @param {Array} quoteItems - Array of quote items
 * @param {boolean} useImperialUnits - Whether to use imperial units for organisation
 * @returns {Array} Array of dimension objects with converted units
 */
const quoteItemsDimensions = (quoteItems, useImperialUnits) => {
    /**
     * Converts a dimension to organisation units.
     * @param {number} value - The dimension value
     * @param {boolean} valueIsImperial - Whether the value is in imperial units
     * @returns {number} Converted dimension
     */
    const convertToOrgUnits = (value, valueIsImperial) => {
        if (useImperialUnits === valueIsImperial) return value
        return useImperialUnits ? mmToInches(value) : inchesToMm(value)
    }

    return quoteItems.map((quoteItem) => {
        const { isImperial, minimumBoundBoxHeight, minimumBoundBoxWidth, rotaryPartLength, sheet } = quoteItem

        const isRotaryPart = 'profile' in quoteItem && Boolean(quoteItem?.profile)

        const rotaryValues = [sheet?.sheetWidth, sheet?.sheetHeight, sheet?.diameter, rotaryPartLength].filter(
            (val) => val > 0
        )
        const flatValues = [minimumBoundBoxWidth, minimumBoundBoxHeight].filter((val) => val > 0)

        const long = isRotaryPart ? Math.max(...rotaryValues) : Math.max(...flatValues)
        const short = isRotaryPart ? getRotaryNthShortestDimension(quoteItem, 2) : Math.min(...flatValues)

        return {
            long,
            short,

            isImperial,
            longInOrgUnits: convertToOrgUnits(long, isImperial),
            shortInOrgUnits: convertToOrgUnits(short, isImperial),
        }
    })
}

/**
 * Gets the largest dimensions from a collection of quote items, formatted for display.
 * @param {Array} quoteItems - Array of quote items
 * @param {Object} organisation - Organisation object with locale and unit preferences
 * @returns {Object} Object with formatted long and short dimensions
 * @example
 * getLargestDimensions(quoteItems, organisation) // returns {long: '100.5 mm', short: '50.2 mm'}
 */
export const getLargestDimensions = (quoteItems, organisation) => {
    if (!quoteItems?.length || !organisation) {
        return { long: '', short: '' }
    }

    const orgIsImperial = organisationIsImperial(organisation)
    const dimensions = quoteItemsDimensions(quoteItems, orgIsImperial)

    /**
     * Finds the largest dimension value across all items for a given key.
     * @param {string} key - The dimension key to compare
     * @returns {number} The largest dimension value
     */
    const getLargestDimensionValue = (key) => {
        return Math.max(...dimensions.map((item) => item[key]))
    }

    const largestLongValue = getLargestDimensionValue('longInOrgUnits')
    const largestShortValue = getLargestDimensionValue('shortInOrgUnits')

    const unitLabel = orgIsImperial ? 'in' : 'mm'
    const formatDimension = (dimension) => `${formatNumber(dimension, organisation.locale, orgIsImperial)} ${unitLabel}`

    return {
        long: formatDimension(largestLongValue),
        short: formatDimension(largestShortValue),
    }
}

/**
 * Converts seconds to Hours, Minutes, Seconds format. Never rolls over into days -
 * durations of 24h or more are still expressed as hours (e.g. "36h 15m 0s"), since
 * "a day" is ambiguous between a 24h period and a working shift.
 * @param {number} seconds - Number of seconds to convert
 * @returns {string} Time formatted as "XhYmZs" or em dash for zero
 * @example
 * secondsToHMS(90061) // returns '25h 1m 1s'
 * secondsToHMS(0) // returns '—'
 */
export const secondsToHMS = (seconds) => {
    if (seconds === 0) {
        return String.fromCharCode(8212) // em dash
    }

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    return `${hours}h ${minutes}m ${secs}s`
}

/**
 * Converts minutes to Hours, Minutes, Seconds format. Thin wrapper around
 * {@link secondsToHMS} for call sites that already hold a minute value.
 * @param {number} minutes - Number of minutes to convert
 * @returns {string} Time formatted as "XhYmZs" or em dash for zero
 * @example
 * minutesToHMS(36.25) // returns '0h 36m 15s'
 */
export const minutesToHMS = (minutes) => secondsToHMS(minutes * 60)

/**
 * Extracts number formatting information for a given locale.
 * @param {string} locale - The locale identifier
 * @returns {Object} Object containing formatting information (decimal, group, decimalPlaces, etc.)
 * @example
 * getNumberFormat('en-US') // returns {decimal: '.', group: ',', ...}
 */
export function getNumberFormat(locale) {
    const formatter = new Intl.NumberFormat(locale)

    const numberFormat = {}
    formatter.formatToParts(10000.01).forEach((item) => {
        numberFormat[item.type] = item.value
    })

    numberFormat['decimal'] = numberFormat['group'] === '.' ? ',' : '.'
    numberFormat['decimalPlaces'] = numberFormat['fraction'] ? numberFormat['fraction'].length : 0

    return numberFormat
}

/**
 * Updates the favicon element with a new source URL.
 * @param {string} src - The new favicon URL
 * @example
 * loadWebStoreFavicon('/new-favicon.ico')
 */
export function loadWebStoreFavicon(src) {
    const favicon = document.getElementById('favicon')
    if (favicon && 'href' in favicon) {
        favicon.href = src
    }
}
